import React, { useRef, useState, useEffect } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Settings, Wrench, Rocket, QrCode, CheckCircle, ArrowRight, Play, Zap, Target, BarChart3 } from 'lucide-react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'
import { useTranslation } from 'react-i18next'

const Build = () => {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, { once: true, margin: "-100px" })
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [0, -50])
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0])
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3)
    }, 3000)
    return () => clearInterval(timer)
  }, [])

  const buildSteps = [
    {
      number: "01",
      title: t('build.steps.0.title'),
      description: t('build.steps.0.description'),
      details: t('build.steps.0.details'),
      icon: <Settings className="w-8 h-8" />,
      gradient: "from-blue-500 to-purple-600"
    },
    {
      number: "02", 
      title: t('build.steps.1.title'),
      description: t('build.steps.1.description'),
      details: t('build.steps.1.details'),
      icon: <Wrench className="w-8 h-8" />,
      gradient: "from-purple-500 to-pink-600"
    },
    {
      number: "03",
      title: t('build.steps.2.title'),
      description: t('build.steps.2.description'),
      details: t('build.steps.2.details'),
      icon: <Rocket className="w-8 h-8" />,
      gradient: "from-pink-500 to-red-600"
    }
  ]

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: t('build.features.0.title'),
      description: t('build.features.0.description')
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: t('build.features.1.title'),
      description: t('build.features.1.description')
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: t('build.features.2.title'),
      description: t('build.features.2.description')
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: t('build.features.3.title'),
      description: t('build.features.3.description')
    }
  ]

  return (
    <div ref={containerRef} className="min-h-screen">
      {/* Hero Section - Dark Gradient Background */}
      <section className="relative min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 overflow-hidden flex items-center justify-center py-20">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-400/30 text-orange-300 text-sm font-medium mb-8"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-2 h-2 bg-orange-400 rounded-full mr-2"
            />
            <span>{t('build.hero.badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t('build.hero.title1')}{' '}
            <span className="block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              {t('build.hero.title2')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-8"
          >
            {t('build.hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl p-6 mb-12 max-w-2xl mx-auto"
          >
            <h3 className="text-lg font-semibold text-orange-300 mb-2">{t('build.hero.announcementTitle')}</h3>
            <p className="text-gray-300">
              {t('build.hero.announcementText')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link to="/inquiry">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(251, 146, 60, 0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 inline-flex items-center space-x-2"
              >
                <span>{t('build.hero.button')}</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Build Steps Section - Light Gray Background */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('build.process.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('build.process.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {buildSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className={`relative bg-white rounded-2xl p-8 shadow-xl overflow-hidden transition-all duration-300 ${
                  activeStep === index ? 'ring-2 ring-purple-500 scale-105' : ''
                }`}
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${step.gradient} flex items-center justify-center text-white mb-6`}>
                  {step.icon}
                </div>
                
                <div className="flex items-center mb-4">
                  <span className={`text-6xl font-bold bg-gradient-to-r ${step.gradient} bg-clip-text text-transparent mr-4`}>
                    {step.number}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                </div>
                
                <p className="text-gray-700 mb-4 font-medium">{step.description}</p>
                <p className="text-gray-600 text-sm">{step.details}</p>
                
                <div className="absolute top-4 right-4">
                  <CheckCircle className={`w-6 h-6 ${activeStep === index ? 'text-green-500' : 'text-gray-300'}`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - White Background */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('buildPage.whyChoose.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('buildPage.whyChoose.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Gradient Background */}
      <section className="relative py-20 bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-10 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('buildPage.cta.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('buildPage.cta.description')}
            </p>
            <Link to="/inquiry">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 mx-auto"
              >
                <span>{t('buildPage.cta.button')}</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Build